import { FetchArgs, FetchBaseQueryError, QueryReturnValue } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

import { RequestMetadata } from "./types.http";

/**
 * Generates request metadata for logging
 *
 * @returns Request metadata with unique ID and timestamp
 */
export const generateRequestMetadata = (): RequestMetadata => {
  return {
    requestId: crypto.randomUUID(),
    timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss")
  };
};

/**
 * Logs request details for debugging
 *
 * @param args - Request arguments
 * @param metadata - Request metadata
 */
export const logRequest = (args: FetchArgs | string, metadata: RequestMetadata, enabled: boolean = true): void => {
  if (import.meta.env.MODE === "development" && enabled) {
    const url = typeof args === "string" ? args : args.url;
    const method = typeof args === "string" ? "GET" : args.method || "GET";
    const body = typeof args === "object" && args.body ? args.body : undefined;

    console.groupCollapsed(`🔵 [${metadata.timestamp}] [${metadata.requestId}] ${method} ${url}`);
    console.log("Request ID:", metadata.requestId);
    console.log("Method:", method);
    console.log("URL:", url);
    if (body) {
      console.log("Body:", body);
    }
    console.groupEnd();
  }
};

/**
 * Logs response details for debugging
 *
 * @param result - Response result
 * @param metadata - Request metadata
 */
export const logResponse = (result: QueryReturnValue<unknown, FetchBaseQueryError, object>, metadata: RequestMetadata, enabled: boolean = true): void => {
  if (import.meta.env.MODE === "development" && enabled) {
    const startTime = dayjs(metadata.timestamp);
    const endTime = dayjs();
    const duration = endTime.diff(startTime, "millisecond");

    console.groupCollapsed(
      result.error ? `🔴 [${metadata.timestamp}] [${metadata.requestId}] Error (${duration}ms)` : `🟢 [${metadata.timestamp}] [${metadata.requestId}] Success (${duration}ms)`
    );

    if (result.error) {
      console.error("Error:", result.error);
      if ("status" in result.error) {
        console.error("Status:", result.error.status);
      }
      if ("data" in result.error) {
        console.error("Error Data:", result.error.data);
      }
    } else {
      console.log("Response:", result.data);
    }

    console.log("Duration:", `${duration}ms`);
    console.groupEnd();
  }
};
